/* eslint-disable react-refresh/only-export-components */
/* =====================================================
   Numdle Game State Management Hook – Version 2.1 (Modified: July 31, 2025)
   Date Modified: 31 July 2025 | Time: Current
   🔄 Changes from Version 1.9:
     - Converted from global variables to React Context + hooks
     - Centralized state management with useReducer pattern
     - Optimized performance with useMemo and useCallback
     - Enhanced separation of concerns
===================================================== */

import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { generateBestEquation, arraysMatch } from '../utils/gameLogic'
import { NUMBERS_SET, TARGET } from '../constants/gameConstants'

// ==========================================
// GAME STATE CONTEXT
// ==========================================

const GameContext = createContext()

/**
 * Game state provider component
 * Manages all game state and provides actions to child components
 */
export function GameProvider({ children }) {
  // ==========================================
  // CORE GAME STATE
  // ==========================================
  
  // Current row (attempt) being played (0-5)
  const [currentRow, setCurrentRow] = useState(0)
  // Current slot position within the active row (0-10)
  const [currentIndex, setCurrentIndex] = useState(0)
  // Array of used numbers for current equation
  const [usedNumbers, setUsedNumbers] = useState([])
  // Grid data: 6 rows × 11 slots each
  const [grid, setGrid] = useState(() => 
    Array(6).fill(null).map(() => Array(11).fill({ value: '', type: '' }))
  )
  // Feedback message and color
  const [feedback, setFeedback] = useState({ message: '', color: '' })
  // Equation preview and calculation steps
  const [tracker, setTracker] = useState('')
  // Toggle state for best solution visibility
  const [showBestSolution, setShowBestSolution] = useState(false)

  // ==========================================
  // COMPUTED VALUES
  // ==========================================
  
  /**
   * Pre-calculated optimal solution using brute-force search
   * Memoized to avoid recalculation on every render
   */
  const bestEquation = useMemo(() => {
    return generateBestEquation(NUMBERS_SET, TARGET)
  }, [])

  /**
   * Current equation slots for the active row
   */
  const currentRowSlots = useMemo(() => {
    return grid[currentRow] || []
  }, [grid, currentRow])

  // ==========================================
  // GAME ACTIONS
  // ==========================================

  /**
   * Place a selected number or operator into the current slot position
   * Validates slot type matching and prevents overwriting filled slots
   */
  const placeValue = useCallback((value, type) => {
    if (currentIndex >= 11) return false

    const expectedType = currentIndex % 2 === 0 ? 'number' : 'operator'
    if (type !== expectedType) return false

    const currentSlot = currentRowSlots[currentIndex]
    if (currentSlot.value) return false // Slot already filled

    setGrid(prevGrid => {
      const newGrid = [...prevGrid]
      newGrid[currentRow] = [...newGrid[currentRow]]
      newGrid[currentRow][currentIndex] = { value, type }
      return newGrid
    })

    if (type === 'number') {
      setUsedNumbers(prev => [...prev, parseInt(value)])
    }

    setCurrentIndex(prev => prev + 1)
    return true
  }, [currentRow, currentIndex, currentRowSlots])

  /**
   * Undo the last number or operator placement
   * Removes value from current slot and makes number buttons available again
   */
  const undoLastPlacement = useCallback(() => {
    if (currentIndex === 0) return

    const prevIndex = currentIndex - 1
    const slotToUndo = currentRowSlots[prevIndex]
    
    setGrid(prevGrid => {
      const newGrid = [...prevGrid]
      newGrid[currentRow] = [...newGrid[currentRow]]
      newGrid[currentRow][prevIndex] = { value: '', type: '' }
      return newGrid
    })

    if (slotToUndo.type === 'number') {
      setUsedNumbers(prev => prev.filter(num => num !== parseInt(slotToUndo.value)))
    }

    setCurrentIndex(prevIndex)
    setFeedback({ message: '', color: '' })
  }, [currentIndex, currentRow, currentRowSlots])

  /**
   * Reset the entire game to initial state
   * Clears all slots, resets position counters, and makes all numbers available
   */
  const resetGame = useCallback(() => {
    setGrid(Array(6).fill(null).map(() => Array(11).fill({ value: '', type: '' })))
    setCurrentRow(0)
    setCurrentIndex(0)
    setUsedNumbers([])
    setFeedback({ message: '', color: '' })
    setTracker('')
  }, [])

  /**
   * Submit the current equation row for validation and scoring
   * Performs comprehensive validation, calculates result, and provides feedback
   */
  const submitEquation = useCallback(() => {
    const tokens = currentRowSlots.map(slot => slot.value).filter(Boolean)
    
    // Validate equation is complete (6 numbers + 5 operators = 11 tokens)
    if (tokens.length !== 11) {
      setFeedback({ 
        message: '⚠️ Fill all 6 numbers and 5 operators.', 
        color: '#d83b01' 
      })
      return
    }

    // Validate that all 6 numbers are used exactly once
    const usedNums = currentRowSlots
      .filter((slot, index) => index % 2 === 0)
      .map(slot => parseInt(slot.value))
      .sort((a, b) => a - b)
    
    const requiredNums = [...NUMBERS_SET].sort((a, b) => a - b)
    
    if (!arraysMatch(usedNums, requiredNums)) {
      setFeedback({ 
        message: '🚫 Use all 6 numbers once.', 
        color: '#d83b01' 
      })
      return
    }

    // Calculate equation result using left-to-right evaluation
    let result = parseInt(tokens[0])
    let preview = `Row ${currentRow + 1}: ${result}`
    let isValid = true

    for (let i = 1; i < tokens.length && isValid; i += 2) {
      const operator = tokens[i]
      const number = parseInt(tokens[i + 1])

      switch (operator) {
        case '+':
          result += number
          break
        case '-':
          result -= number
          break
        case '*':
          result *= number
          break
        case '/':
          if (number === 0 || result % number !== 0) {
            setFeedback({ 
              message: '❌ Invalid division step.', 
              color: '#d83b01' 
            })
            setTracker(prev => prev + `${preview}\n`)
            isValid = false
            return
          }
          result = Math.floor(result / number)
          break
        default:
          setFeedback({ 
            message: `⚠️ Unknown operator "${operator}"`, 
            color: '#d83b01' 
          })
          isValid = false
          return
      }
      preview += ` → ${result}`
    }

    if (!isValid) return

    // Compare with optimal solution and apply visual feedback
    setGrid(prevGrid => {
      const newGrid = [...prevGrid]
      newGrid[currentRow] = newGrid[currentRow].map((slot, index) => ({
        ...slot,
        feedback: slot.value === bestEquation[index] ? 'correct' : 'incorrect'
      }))
      return newGrid
    })

    // Display calculation results and provide feedback
    setTracker(prev => prev + `${preview}\n`)
    
    const isSuccess = result === TARGET
    setFeedback({
      message: isSuccess 
        ? '🎉 Success! Target matched.' 
        : `❌ Incorrect. Result = ${result}, target = ${TARGET}`,
      color: isSuccess ? 'green' : '#d83b01'
    })

    // Reset for next attempt or end game
    setUsedNumbers([])
    
    if (currentRow < 5) {
      setCurrentRow(prev => prev + 1)
      setCurrentIndex(0)
      setTracker(prev => prev + `Try again in Row ${currentRow + 2}\n`)
    } else {
      setFeedback(prev => ({ 
        ...prev, 
        message: prev.message + ' 🔚 All rows used.' 
      }))
    }
  }, [currentRow, currentRowSlots, bestEquation])

  // ==========================================
  // REAL-TIME EQUATION PREVIEW
  // ==========================================

  /**
   * Update real-time preview of the current partial equation
   * Shows step-by-step calculation as players build their equation
   */
  useEffect(() => {
    const tokens = currentRowSlots.map(slot => slot.value).filter(Boolean)
    
    // Need at least number-operator-number to show preview
    if (tokens.length < 3) {
      setTracker('')
      return
    }

    let result = parseInt(tokens[0])
    let preview = `Row ${currentRow + 1} (preview): ${result}`

    // Calculate partial result for complete operator-number pairs
    for (let i = 1; i < tokens.length - 1; i += 2) {
      const operator = tokens[i]
      const number = parseInt(tokens[i + 1])
      
      if (!operator || isNaN(number)) break

      switch (operator) {
        case '+':
          result += number
          break
        case '-':
          result -= number
          break
        case '*':
          result *= number
          break
        case '/':
          if (number === 0 || result % number !== 0) {
            setTracker('⚠️ Invalid division')
            return
          }
          result = Math.floor(result / number)
          break
        default:
          setTracker('⚠️ Unknown operator')
          return
      }
      preview += ` → ${result}`
    }
    
    setTracker(preview)
  }, [currentRowSlots, currentRow])

  // ==========================================
  // CONTEXT VALUE
  // ==========================================

  const value = useMemo(() => ({
    // State
    NUMBERS_SET,
    TARGET,
    currentRow,
    currentIndex,
    usedNumbers,
    grid,
    feedback,
    tracker,
    showBestSolution,
    bestEquation,
    currentRowSlots,
    
    // Actions
    placeValue,
    undoLastPlacement,
    resetGame,
    submitEquation,
    setShowBestSolution,
  }), [
    currentRow,
    currentIndex,
    usedNumbers,
    grid,
    feedback,
    tracker,
    showBestSolution,
    bestEquation,
    currentRowSlots,
    placeValue,
    undoLastPlacement,
    resetGame,
    submitEquation,
  ])

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  )
}

/**
 * Custom hook to access game state and actions
 * Must be used within a GameProvider
 */
export function useGameState() {
  const context = useContext(GameContext)
  if (!context) {
    throw new Error('useGameState must be used within a GameProvider')
  }
  return context
}